import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useReaderAuth } from '../../../context/ReaderAuthContext';
import { 
  BookOpen, ChevronLeft, ChevronRight, Menu, X, Settings, 
  Bookmark, BookmarkCheck, Moon, Sun, List,
  Loader2, Home, Type, AlignJustify, Lock, ShoppingCart
} from 'lucide-react';
import SEOMeta, { BOOK_META } from '../../../components/SEOMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01';
const BOOK_SLUG = 'cultural-fluency-without-compromise';
const BOOK_COVER_URL = 'https://customer-assets.emergentagent.com/job_d368e4dd-685c-4244-ae95-a5da53cd41bc/artifacts/op7w644a_font%20cover%20jpg.jpg';

// Free preview chapters - check by title or chapter number
const isPreviewChapterByTitle = (title, chapterNumber, isFrontMatter) => {
  if (!title) return false;
  const lowerTitle = title.toLowerCase();
  // Front matter is always free preview
  if (isFrontMatter) return true;
  if (lowerTitle.includes('front matter')) return true;
  if (lowerTitle.includes('preface')) return true;
  // Use word boundary checks to avoid false positives (e.g., "Discovering" matching "cover")
  if (lowerTitle === 'cover' || lowerTitle.startsWith('cover ') || lowerTitle.includes(' cover')) return true;
  if (lowerTitle.includes('dedication')) return true;
  if (lowerTitle.includes('copyright')) return true;
  if (lowerTitle.includes('advance praise')) return true;
  // Only chapter 1 is free
  if (chapterNumber === '1' || chapterNumber === 1) return true;
  return false;
};

const BookReaderPage = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, hasEbookAccess, isWhitelisted, isLoading: authLoading } = useReaderAuth();
  
  // Content state
  const [chapter, setChapter] = useState(null);
  const [toc, setToc] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCover, setShowCover] = useState(false);
  
  // Preview state
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewLimitReached, setPreviewLimitReached] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Reader state
  const [showToc, setShowToc] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    theme: 'light',
    fontSize: 18,
    lineSpacing: 1.6,
    readerMode: 'scroll',
    fontFamily: 'Georgia'
  });
  
  // Bookmarks
  const [bookmarks, setBookmarks] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  
  // Scroll tracking
  const contentRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Theme styles
  const themes = {
    light: { bg: '#faf8f5', text: '#2c3e50', accent: '#d4af37' },
    dark: { bg: '#1a1a2e', text: '#e0e0e0', accent: '#d4af37' },
    sepia: { bg: '#f4ecd8', text: '#5c4b37', accent: '#8b7355' }
  };

  const currentTheme = themes[settings.theme] || themes.light;

  // Check if chapter is available for free preview using TOC
  const isPreviewChapter = (chId, tocData = toc) => {
    if (!chId) return false;
    const chapter = tocData.find(c => c.id === chId);
    if (!chapter) return false;
    return isPreviewChapterByTitle(chapter.title, chapter.number, chapter.is_front_matter);
  };

  // Determine access mode and fetch content
  useEffect(() => {
    const fetchContent = async () => {
      if (!chapterId) {
        // No chapter selected - show cover page
        setShowCover(true);
        setIsLoading(true);
        await fetchToc();
        setIsLoading(false);
        return;
      }
      
      setShowCover(false);
      setIsLoading(true);
      setError('');
      
      try {
        // First fetch TOC to determine if this is a preview chapter
        let tocData = toc;
        if (toc.length === 0) {
          const tocRes = await fetch(`${API_URL}/api/ebook/metadata`);
          if (tocRes.ok) {
            const data = await tocRes.json();
            tocData = data.table_of_contents || [];
            setToc(tocData);
          }
        }
        
        const hasPurchased = isAuthenticated() && hasEbookAccess();
        const canPreview = isPreviewChapter(chapterId, tocData);
        
        // Determine which endpoint to use
        let chapterRes;
        
        if (hasPurchased && token) {
          // User has access - fetch full content
          chapterRes = await fetch(`${API_URL}/api/ebook/chapter/${chapterId}/full`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setIsPreviewMode(false);
        } else if (canPreview) {
          // Free preview - fetch limited content
          chapterRes = await fetch(`${API_URL}/api/ebook/chapter/${chapterId}`);
          setIsPreviewMode(true);
        } else {
          // Not a preview chapter and no access - show paywall
          setShowPaywall(true);
          setIsLoading(false);
          await fetchToc();
          return;
        }
        
        if (!chapterRes.ok) {
          if (chapterRes.status === 403) {
            setShowPaywall(true);
            await fetchToc();
            setIsLoading(false);
            return;
          }
          throw new Error('Failed to load chapter');
        }
        
        const chapterData = await chapterRes.json();
        
        // Handle redirect response (when section ID is requested instead of chapter)
        if (chapterData.redirect && chapterData.redirect_to) {
          navigate(`/books/${BOOK_SLUG}/read/${chapterData.redirect_to}`, { replace: true });
          return;
        }
        
        setChapter(chapterData);
        setPreviewLimitReached(chapterData.preview_limit_reached || false);
        
        // If preview limit reached, show paywall after content
        if (chapterData.preview_limit_reached) {
          setShowPaywall(true);
        }
        
        // Fetch TOC
        await fetchToc();
        
        // Fetch user-specific data if authenticated
        if (token && hasPurchased) {
          await fetchUserData();
        }
        
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchContent();
  // fetchToc / fetchUserData / canPreview are stable helpers referencing only
  // module constants and stable refs — safe to omit from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chapterId, isAuthenticated, hasEbookAccess]);

  const fetchTocAndRedirect = async () => {
    try {
      const tocRes = await fetch(`${API_URL}/api/ebook/metadata`);
      if (tocRes.ok) {
        const tocData = await tocRes.json();
        setToc(tocData.table_of_contents || []);
        // Redirect to cover page first (order: -4), or first chapter
        const sortedToc = [...(tocData.table_of_contents || [])].sort((a, b) => a.order - b.order);
        const firstItem = sortedToc[0];
        if (firstItem) {
          navigate(`/books/${BOOK_SLUG}/read/${firstItem.id}`, { replace: true });
        }
      }
    } catch (e) {
      console.error('Failed to load TOC:', e);
    }
    setIsLoading(false);
  };

  const fetchToc = async () => {
    try {
      const tocRes = await fetch(`${API_URL}/api/ebook/metadata`);
      if (tocRes.ok) {
        const tocData = await tocRes.json();
        setToc(tocData.table_of_contents || []);
      }
    } catch (e) {
      console.error('Failed to load TOC:', e);
    }
  };

  const fetchUserData = async () => {
    try {
      // Fetch bookmarks
      const bookmarksRes = await fetch(`${API_URL}/api/ebook/bookmarks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bookmarksRes.ok) {
        const bms = await bookmarksRes.json();
        setBookmarks(bms);
        setIsBookmarked(bms.some(b => b.chapter_id === chapterId));
      }
      
      // Fetch settings
      const settingsRes = await fetch(`${API_URL}/api/ebook/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...s }));
      }
    } catch (e) {
      console.error('Failed to fetch user data:', e);
    }
  };

  // Save progress on scroll (only for authenticated users)
  const handleScroll = useCallback(() => {
    if (!contentRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setScrollProgress(Math.min(progress, 100));
    
    // Only save progress for authenticated users with access
    if (!token || isPreviewMode) return;
    
    clearTimeout(window.progressTimeout);
    window.progressTimeout = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/ebook/progress?chapter_id=${chapterId}&scroll_position=${progress}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Failed to save progress:', e);
      }
    }, 2000);
  // API_URL is a module constant; contentRef is a stable React ref — safe to omit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chapterId, isPreviewMode]);

  // Toggle bookmark (only for authenticated users)
  const toggleBookmark = async () => {
    if (!token || isPreviewMode) return;
    
    try {
      if (isBookmarked) {
        const bookmark = bookmarks.find(b => b.chapter_id === chapterId);
        if (bookmark) {
          await fetch(`${API_URL}/api/ebook/bookmark/${bookmark.id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setBookmarks(prev => prev.filter(b => b.id !== bookmark.id));
          setIsBookmarked(false);
        }
      } else {
        const res = await fetch(`${API_URL}/api/ebook/bookmark?chapter_id=${chapterId}&position=${scrollProgress}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBookmarks(prev => [...prev, { id: data.id, chapter_id: chapterId }]);
          setIsBookmarked(true);
        }
      }
    } catch (e) {
      console.error('Failed to toggle bookmark:', e);
    }
  };

  // Update settings
  const updateSetting = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    if (!token || isPreviewMode) return;
    
    try {
      await fetch(`${API_URL}/api/ebook/settings?${key}=${value}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Failed to save settings:', e);
    }
  };

  // Navigation
  const chapters = toc.filter(item => item.type === 'chapter');
  const currentIndex = chapters.findIndex(c => c.id === chapterId);
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;

  // Check if next chapter requires purchase
  const nextChapterLocked = nextChapter && !isPreviewChapter(nextChapter.id) && !hasEbookAccess();

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: currentTheme.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: currentTheme.accent }} />
      </div>
    );
  }

  // Show cover page when no chapter is selected
  if (showCover) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: currentTheme.bg, color: currentTheme.text }}>
        <SEOMeta 
          url="/books/cultural-fluency-without-compromise/read"
          title={BOOK_META.title}
          description={BOOK_META.description}
          image={BOOK_META.image}
          imageAlt={BOOK_META.imageAlt}
          type="book"
        />
        {/* Header */}
        <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: currentTheme.bg, borderColor: `${currentTheme.text}20` }}>
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowToc(true)}
                  className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                  aria-label="Table of Contents"
                  data-testid="toc-btn"
                >
                  <Menu size={20} />
                </button>
                <Link to={`/books/${BOOK_SLUG}`} className="p-2 rounded-lg hover:bg-black/5 transition-colors">
                  <Home size={20} />
                </Link>
              </div>
              
              <div className="flex items-center gap-2 px-3 py-1 bg-[#d4af37]/10 rounded-full">
                <BookOpen size={14} className="text-[#d4af37]" />
                <span className="text-xs font-medium text-[#d4af37]">Preview</span>
              </div>
              
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                aria-label="Settings"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Cover Content */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          <div className="max-w-md w-full text-center">
            <img 
              src={BOOK_COVER_URL}
              alt="Cultural Fluency Without Compromise - Book Cover"
              className="w-full max-w-sm mx-auto rounded-lg shadow-2xl mb-8"
            />
            <h1 className="text-2xl font-bold font-serif mb-2">Cultural Fluency Without Compromise</h1>
            <p className="text-sm opacity-70 mb-6">Thriving Across Cultures with Presence and Integrity</p>
            <p className="text-xs opacity-50 mb-8">by Promise Monday</p>
            
            {chapters.length > 0 && (
              <button
                onClick={() => navigate(`/books/${BOOK_SLUG}/read/${chapters[0].id}`)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors"
                style={{ backgroundColor: currentTheme.accent, color: '#1a365d' }}
                data-testid="start-reading-btn"
              >
                <BookOpen size={18} />
                Start Reading
              </button>
            )}
          </div>
        </main>

        {/* TOC Sidebar */}
        {showToc && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowToc(false)} />
            <div 
              className="absolute left-0 top-0 h-full w-80 max-w-[85vw] overflow-y-auto"
              style={{ backgroundColor: currentTheme.bg }}
            >
              <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: `${currentTheme.text}20` }}>
                <h2 className="font-semibold">Contents</h2>
                <button onClick={() => setShowToc(false)} className="p-2 rounded-lg hover:bg-black/5">
                  <X size={20} />
                </button>
              </div>
              <nav className="p-4">
                {toc.map((item) => {
                  const isChapter = item.type === 'chapter';
                  const isLocked = !isPreviewChapterByTitle(item.title, item.number, item.is_front_matter) && !hasEbookAccess();
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (!isLocked || isPreviewChapterByTitle(item.title, item.number, item.is_front_matter)) {
                          navigate(`/books/${BOOK_SLUG}/read/${item.id}`);
                          setShowToc(false);
                        }
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                        isChapter ? 'font-medium' : 'text-sm pl-6 opacity-80'
                      } ${isLocked && isChapter ? 'opacity-50' : 'hover:bg-black/5'}`}
                    >
                      <span className={isLocked ? 'opacity-60' : ''}>
                        {item.number && `${item.number}. `}{item.title}
                      </span>
                      {isLocked && isChapter && <Lock size={14} className="opacity-40" />}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowSettings(false)} />
            <div 
              className="absolute right-0 top-0 h-full w-80 max-w-[85vw] overflow-y-auto"
              style={{ backgroundColor: currentTheme.bg }}
            >
              <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: `${currentTheme.text}20` }}>
                <h2 className="font-semibold">Settings</h2>
                <button onClick={() => setShowSettings(false)} className="p-2 rounded-lg hover:bg-black/5">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 space-y-6">
                {/* Theme */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Theme</label>
                  <div className="flex gap-2">
                    {['light', 'dark', 'sepia'].map((theme) => (
                      <button
                        key={theme}
                        onClick={() => updateSetting('theme', theme)}
                        className={`flex-1 py-2 px-3 rounded-lg border-2 capitalize text-sm transition-colors ${
                          settings.theme === theme 
                            ? 'border-[#d4af37]' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        style={{ 
                          backgroundColor: themes[theme].bg,
                          color: themes[theme].text
                        }}
                      >
                        {theme}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Type size={16} />
                    Font Size: {settings.fontSize}px
                  </label>
                  <input
                    type="range"
                    min="14"
                    max="24"
                    value={settings.fontSize}
                    onChange={(e) => updateSetting('fontSize', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Line Spacing */}
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <AlignJustify size={16} />
                    Line Spacing: {settings.lineSpacing}
                  </label>
                  <input
                    type="range"
                    min="1.2"
                    max="2"
                    step="0.1"
                    value={settings.lineSpacing}
                    onChange={(e) => updateSetting('lineSpacing', parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: currentTheme.bg }}>
        <div className="text-center p-8">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to={`/books/${BOOK_SLUG}`} className="text-[#d4af37] hover:underline">
            Back to Book
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: currentTheme.bg, color: currentTheme.text }}>
      {/* Header */}
      <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: currentTheme.bg, borderColor: `${currentTheme.text}20` }}>
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowToc(true)}
                className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                aria-label="Table of Contents"
                data-testid="toc-btn"
              >
                <Menu size={20} />
              </button>
              <Link to={`/books/${BOOK_SLUG}`} className="p-2 rounded-lg hover:bg-black/5 transition-colors">
                <Home size={20} />
              </Link>
            </div>
            
            {/* Preview Badge */}
            {isPreviewMode && (
              <div className="flex items-center gap-2 px-3 py-1 bg-[#d4af37]/10 rounded-full">
                <BookOpen size={14} className="text-[#d4af37]" />
                <span className="text-xs font-medium text-[#d4af37]">Preview</span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              {!isPreviewMode && (
                <button
                  onClick={toggleBookmark}
                  className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                  aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                  data-testid="bookmark-btn"
                >
                  {isBookmarked ? (
                    <BookmarkCheck size={20} style={{ color: currentTheme.accent }} />
                  ) : (
                    <Bookmark size={20} />
                  )}
                </button>
              )}
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                aria-label="Settings"
                data-testid="settings-btn"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-0.5 bg-black/10">
          <div 
            className="h-full transition-all duration-300"
            style={{ width: `${scrollProgress}%`, backgroundColor: currentTheme.accent }}
          />
        </div>
      </header>

      {/* Content */}
      <main 
        ref={contentRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
        style={{ 
          fontSize: `${settings.fontSize}px`,
          lineHeight: settings.lineSpacing,
          fontFamily: settings.fontFamily
        }}
      >
        <article className="max-w-2xl mx-auto px-6 py-8">
          {/* Cover Page - Special rendering */}
          {chapter?.id === 'cover' ? (
            <div className="flex flex-col items-center justify-center min-h-[70vh]">
              <img 
                src="https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/230tiak3_font%20cover%20jpg.jpg"
                alt="Cultural Fluency Without Compromise - Book Cover"
                className="max-w-full max-h-[65vh] rounded-lg shadow-2xl"
              />
              <p className="mt-6 text-sm opacity-60">Tap &ldquo;Next&rdquo; to begin reading</p>
            </div>
          ) : (
            <>
              {/* Chapter Header */}
              <header className="mb-8 text-center">
                {chapter?.chapter_number && (
                  <p className="text-sm opacity-60 mb-2">Chapter {chapter.chapter_number}</p>
                )}
                <h1 className="text-2xl font-bold font-display">{chapter?.title}</h1>
                {chapter?.subtitle && (
                  <p className="mt-2 opacity-80">{chapter.subtitle}</p>
                )}
              </header>

              {/* Sections */}
              {chapter?.sections?.map((section) => (
                <section key={section.id} className="mb-8">
                  {section.title && (
                    <h2 className="text-xl font-semibold mb-4">{section.section_number} {section.title}</h2>
                  )}
                  
                  {section.paragraphs?.map((para) => {
                    // Handle special content types
                    if (para.content?.startsWith('[COVER_IMAGE:')) {
                      const imageUrl = para.content.match(/\[COVER_IMAGE:(.*?)\]/)?.[1];
                      return (
                        <div key={para.id} className="flex justify-center my-8">
                          <img src={imageUrl} alt="Cultural Fluency Without Compromise - illustration by Dr Promise Monday" className="max-w-full rounded-lg shadow-xl" />
                        </div>
                      );
                    }
                    
                    // Check if this is an endorsement/quote (starts with ")
                    const isQuote = para.content?.startsWith('"') && para.content?.includes('—');
                    
                    if (isQuote) {
                      const parts = para.content.split('—');
                      const quote = parts[0]?.trim();
                      const attribution = parts[1]?.trim();
                      
                      return (
                        <blockquote key={para.id} className="mb-6 pl-4 border-l-2 italic" style={{ borderColor: currentTheme.accent }}>
                          <p className="mb-2">{quote}</p>
                          {attribution && (
                            <cite className="text-sm not-italic opacity-70">— {attribution}</cite>
                          )}
                        </blockquote>
                      );
                    }
                    
                    return (
                      <p key={para.id} className="mb-4 text-justify">
                        {para.content}
                      </p>
                    );
                  })}
                </section>
              ))}
            </>
          )}

          {/* Preview Watermark */}
          {isPreviewMode && chapter?.watermark && (
            <p className="text-center text-xs opacity-30 mt-8 select-none">
              {chapter.watermark}
            </p>
          )}
          
          {/* Full Access Watermark */}
          {!isPreviewMode && chapter?.watermark && (
            <p className="text-center text-xs opacity-30 mt-12 select-none">
              {chapter.watermark}
            </p>
          )}

          {/* Preview Limit Message & Paywall Trigger */}
          {previewLimitReached && (
            <div className="mt-8 p-6 bg-gradient-to-br from-[#2c3e50] to-[#1a252f] rounded-xl text-white text-center">
              <Lock className="w-8 h-8 mx-auto mb-4 text-[#d4af37]" />
              <h3 className="text-xl font-bold mb-2">Preview Complete</h3>
              <p className="text-white/80 mb-4">
                You&apos;ve reached the end of your free preview. Purchase to continue reading.
              </p>
              <a
                href={STRIPE_PAYMENT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#2c3e50]"
                data-testid="preview-buy-btn"
              >
                <ShoppingCart size={18} />
                Buy to Read
              </a>
              <p className="text-white/40 text-xs mt-3">
                Secure checkout powered by Stripe.
              </p>
            </div>
          )}

          {/* Navigation */}
          {!previewLimitReached && (
            <nav className="mt-12 pt-8 border-t flex justify-between" style={{ borderColor: `${currentTheme.text}20` }}>
              {prevChapter ? (
                <Link 
                  to={`/books/${BOOK_SLUG}/read/${prevChapter.id}`}
                  className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft size={20} />
                  <span className="text-sm">Previous</span>
                </Link>
              ) : <div />}
              
              {nextChapter ? (
                nextChapterLocked ? (
                  <button 
                    onClick={() => setShowPaywall(true)}
                    className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity"
                  >
                    <Lock size={16} className="text-[#d4af37]" />
                    <span className="text-sm">Next (Locked)</span>
                    <ChevronRight size={20} />
                  </button>
                ) : (
                  <Link 
                    to={`/books/${BOOK_SLUG}/read/${nextChapter.id}`}
                    className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity"
                  >
                    <span className="text-sm">Next</span>
                    <ChevronRight size={20} />
                  </Link>
                )
              ) : <div />}
            </nav>
          )}
        </article>
      </main>

      {/* Paywall Modal - NEVER show for whitelisted users */}
      {showPaywall && !hasEbookAccess() && !isWhitelisted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPaywall(false)} />
          <div className="relative bg-gradient-to-br from-[#2c3e50] to-[#1a252f] rounded-2xl p-8 max-w-md w-full text-white shadow-2xl">
            <button 
              onClick={() => setShowPaywall(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <X size={24} />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#d4af37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-[#d4af37]" />
              </div>
              
              <h2 className="text-2xl font-bold mb-2 font-display">Unlock Full Access</h2>
              <p className="text-white/70 mb-6">
                Continue reading &ldquo;Cultural Fluency Without Compromise&rdquo; with lifetime access on all your devices.
              </p>
              
              <div className="space-y-3 mb-8 text-left">
                {['Full book access', 'Read on web, iOS & Android', 'Sync progress across devices', 'Bookmarks & highlights'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-white/80">
                    <div className="w-1.5 h-1.5 bg-[#d4af37] rounded-full" />
                    {feature}
                  </div>
                ))}
              </div>
              
              <a
                href={STRIPE_PAYMENT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#d4af37] text-[#2c3e50] px-6 py-4 rounded-lg font-bold text-lg hover:bg-[#e5c349] transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#2c3e50]"
                data-testid="paywall-buy-btn"
              >
                <ShoppingCart size={20} />
                Buy to Read
              </a>
              
              <p className="text-white/40 text-xs mt-4">
                Secure checkout powered by Stripe.
              </p>
              
              {!isAuthenticated() && (
                <p className="text-white/60 text-sm mt-4">
                  Already purchased?{' '}
                  <Link to={`/signin?redirect=/books/${BOOK_SLUG}/library`} className="text-[#d4af37] hover:underline">
                    Sign in
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TOC Sidebar */}
      {showToc && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowToc(false)} />
          <div 
            className="relative w-80 max-w-[85vw] h-full overflow-y-auto"
            style={{ backgroundColor: currentTheme.bg }}
          >
            <div className="sticky top-0 p-4 border-b flex items-center justify-between" style={{ backgroundColor: currentTheme.bg, borderColor: `${currentTheme.text}20` }}>
              <h2 className="font-semibold flex items-center gap-2">
                <List size={18} />
                Contents
              </h2>
              <button onClick={() => setShowToc(false)} className="p-2">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              {chapters.map((ch) => {
                const isLocked = !isPreviewChapter(ch.id) && !hasEbookAccess();
                const isCurrent = ch.id === chapterId;
                
                return (
                  <div key={ch.id}>
                    {isLocked ? (
                      <button
                        onClick={() => {
                          setShowToc(false);
                          setShowPaywall(true);
                        }}
                        className="w-full text-left py-3 px-3 rounded-lg mb-1 flex items-center justify-between opacity-50 hover:opacity-70 transition-opacity"
                      >
                        <span>
                          {ch.number && <span className="text-sm opacity-60 mr-2">{ch.number}.</span>}
                          {ch.title}
                        </span>
                        <Lock size={14} className="text-[#d4af37]" />
                      </button>
                    ) : (
                      <Link
                        to={`/books/${BOOK_SLUG}/read/${ch.id}`}
                        onClick={() => setShowToc(false)}
                        className={`block py-3 px-3 rounded-lg mb-1 transition-colors ${
                          isCurrent ? 'font-medium' : 'opacity-70 hover:opacity-100'
                        }`}
                        style={{ 
                          backgroundColor: isCurrent ? `${currentTheme.accent}20` : 'transparent',
                          color: isCurrent ? currentTheme.accent : currentTheme.text
                        }}
                      >
                        {ch.number && <span className="text-sm opacity-60 mr-2">{ch.number}.</span>}
                        {ch.title}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSettings(false)} />
          <div 
            className="relative w-full md:w-96 md:rounded-2xl overflow-hidden"
            style={{ backgroundColor: currentTheme.bg }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: `${currentTheme.text}20` }}>
              <h2 className="font-semibold flex items-center gap-2">
                <Settings size={18} />
                Reading Settings
              </h2>
              <button onClick={() => setShowSettings(false)} className="p-2">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Theme */}
              <div>
                <label className="text-sm font-medium mb-3 block">Theme</label>
                <div className="flex gap-2">
                  {[
                    { key: 'light', icon: <Sun size={16} />, label: 'Light' },
                    { key: 'sepia', icon: <BookOpen size={16} />, label: 'Sepia' },
                    { key: 'dark', icon: <Moon size={16} />, label: 'Dark' },
                  ].map((theme) => (
                    <button
                      key={theme.key}
                      onClick={() => updateSetting('theme', theme.key)}
                      className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 text-sm transition-all ${
                        settings.theme === theme.key 
                          ? 'ring-2 ring-[#d4af37]' 
                          : 'bg-black/5'
                      }`}
                    >
                      {theme.icon}
                      {theme.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size */}
              <div>
                <label className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Type size={16} />
                  Font Size
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => updateSetting('fontSize', Math.max(12, settings.fontSize - 2))}
                    className="w-10 h-10 rounded-lg bg-black/5 flex items-center justify-center text-lg"
                  >
                    A-
                  </button>
                  <span className="flex-1 text-center">{settings.fontSize}px</span>
                  <button
                    onClick={() => updateSetting('fontSize', Math.min(32, settings.fontSize + 2))}
                    className="w-10 h-10 rounded-lg bg-black/5 flex items-center justify-center text-lg"
                  >
                    A+
                  </button>
                </div>
              </div>

              {/* Line Spacing */}
              <div>
                <label className="text-sm font-medium mb-3 flex items-center gap-2">
                  <AlignJustify size={16} />
                  Line Spacing
                </label>
                <div className="flex gap-2">
                  {[1.4, 1.6, 1.8, 2.0].map((spacing) => (
                    <button
                      key={spacing}
                      onClick={() => updateSetting('lineSpacing', spacing)}
                      className={`flex-1 py-2 rounded-lg text-sm transition-all ${
                        settings.lineSpacing === spacing 
                          ? 'ring-2 ring-[#d4af37]' 
                          : 'bg-black/5'
                      }`}
                    >
                      {spacing}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Family */}
              <div>
                <label className="text-sm font-medium mb-3 block">Font</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Georgia', 'Merriweather', 'Inter', 'system-ui'].map((font) => (
                    <button
                      key={font}
                      onClick={() => updateSetting('fontFamily', font)}
                      className={`py-2 px-3 rounded-lg text-sm transition-all ${
                        settings.fontFamily === font 
                          ? 'ring-2 ring-[#d4af37]' 
                          : 'bg-black/5'
                      }`}
                      style={{ fontFamily: font }}
                    >
                      {font === 'system-ui' ? 'System' : font}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookReaderPage;
